/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/** @module VirtualServiceModule */

import {
    Component,
    Type,
} from '@angular/core';

import {
    IHttpResponse,
    IPromise,
} from 'angular';

import {
    ApplicationProfileType,
    AviPermissionResource,
    IApplicationProfile,
    IHTTPPolicies,
    INetworkSecurityPolicy,
    IService,
    IVirtualService,
    IVsVip,
    VHType,
    VirtualServiceType,
} from 'generated-types';

import {
    debounce,
    extend,
    some,
} from 'underscore';
import { VirtualService } from 'object-types';
import { DevLoggerService } from 'dev-logger-service';
import { naturalSort } from 'ng/shared/utils/natural-sort.utils';
import { IItemEditParams } from 'ajs/modules/data-model/factories/item.factory';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';
import { ObjectTypeItem } from 'ajs/modules/data-model/factories/object-type-item.factory';
import { withFullModalMixin } from 'ajs/js/utilities/mixins/with-full-modal.mixin';
import { TWindowElement } from 'ajs/modules/data-model/data-model.types';
import {
    APPLICATION_PROFILE_COLLECTION_TOKEN,
} from 'ajs/modules/application-profile/application-profile.tokens';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import { IVSDropParams } from 'collections/virtual-service.collection.factory';

import {
    ApplicationProfile,
} from 'ajs/modules/application-profile/factories/application-profile.item.factory';

import {
    ApplicationProfileCollection,
} from 'ajs/modules/application-profile/factories/application-profile.collection.factory';

import { StringService } from 'string-service';
import { L10nService } from '@vmw/ngx-vip';
import {
    BehaviorSubject,
    combineLatest,
    map,
    Observable,
} from 'rxjs';

import * as globalL10n from 'global-l10n';

import {
    VirtualServicePreviewComponent,
} from 'ng/lazy-loaded-components/modals/virtual-service-modal/virtual-service-preview';

import { AnalyticsPolicyConfigItem } from 'message-items/analytics-policy.config-item.factory';

import {
    ContentRewriteProfileConfigItem,
} from 'message-items/content-rewrite-profile.config-item.factory';

import { VHMatchConfigItem } from 'ajs/modules/vs/factories/vh-match.config-item.factory';

import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import { RoleFilterMatchLabelConfigItem } from
    'ajs/modules/data-model/factories/role-filter-match-label.config-item.factory';
import { IBaseRequestPromise } from 'ajs/modules/data-model/factories/base.factory';
import { IItemParams } from 'ajs/js/utilities/mixins';
import { IFullModalLayout } from 'ng/modules/core/services/full-modal';
import { WafPolicyItem } from 'ajs/modules/waf/factories/waf-policy/waf-policy.item.factory';

const { ...globalL10nKeys } = globalL10n;

export enum VirtualServiceTypeEnum {
    VS_TYPE_NORMAL = VirtualServiceType.VS_TYPE_NORMAL,
    VS_TYPE_VH_CHILD = VirtualServiceType.VS_TYPE_VH_CHILD,
    VS_TYPE_VH_PARENT = VirtualServiceType.VS_TYPE_VH_PARENT,
}

export enum VHTypeEnum {
    VS_TYPE_VH_ENHANCED = VHType.VS_TYPE_VH_ENHANCED,
    VS_TYPE_VH_SNI = VHType.VS_TYPE_VH_SNI,
}

/**
 * Partial Type of Virtual Service object.
 */
type TVirtualServicePartial = Omit<IVirtualService,
'analytics_policy' |
'vh_matches' |
'markers' |
'content_rewrite' |
'http_policies'
>;

/**
 * Type of Virtual Service Config object.
 */
interface IVirtualServiceConfig extends TVirtualServicePartial {
    analytics_policy: AnalyticsPolicyConfigItem;
    vh_matches: VHMatchConfigItem;
    markers: RepeatedMessageItem<RoleFilterMatchLabelConfigItem>;
    content_rewrite: ContentRewriteProfileConfigItem;
    // May be added to the config if loaded with join.
    vsvip_ref_data?: IVsVip;
    http_policies?: RepeatedMessageItem<MessageItem<IHTTPPolicies>>;
}

interface IVirtualServiceData {
    config: IVirtualServiceConfig,
    pools?: string[];
    poolgroups?: string[];
}

interface IVsMacroData {
    data: IVirtualService,
    uri_path: string,
}

const APPLICATION_PROFILE_LOADING_DEBOUNCE_VALUE = 200;
const VS_ENDPOINT = '/api/virtualservice';

export class VirtualServiceItem extends
    withEditChildMessageItemMixin(withFullModalMixin(ObjectTypeItem)) {
    public static ngDependencies = [
        'Collection',
        StringService,
        'l10nService',
        'NetworkSecurityPolicy',
        APPLICATION_PROFILE_COLLECTION_TOKEN,
        'HttpPolicySet',
        'HttpPolicy',
        'devLoggerService',
    ];

    /**
     * Function to get Virtual Service config.
     */
    public getConfig: () => IVirtualServiceConfig;

    /**
     * PoolGroup data.
     */
    public data: IVirtualServiceData;

    public httpPolicySets: any;

    public networkSecurityPolicy: MessageItem<INetworkSecurityPolicy>;

    public readonly isApplicationProfileLoaded$: BehaviorSubject<boolean>;

    public readonly onApplicationTypeChange$: BehaviorSubject<boolean>;

    public readonly onVsTypeHostingChange$: BehaviorSubject<VirtualServiceType | undefined>;

    public readonly onVsHostingTypeChange$: BehaviorSubject<VHType | undefined>;

    public readonly onVsNameChange$: BehaviorSubject<string | undefined>;

    public readonly onVrfContextAndCloudSet$: BehaviorSubject<boolean>;

    public readonly onApplicationProfileTypeChange$: BehaviorSubject<ApplicationProfileType>;

    public readonly applicationProfileType$: Observable<ApplicationProfileType>;

    public readonly onWafPolicyItemChange$: BehaviorSubject<WafPolicyItem | undefined>;

    public isSslEnabledInServicePorts: boolean;

    /**
     * DevLoggerService instance.
     */
    protected readonly devLoggerService: DevLoggerService;

    private readonly ApplicationProfileCollection: ApplicationProfileCollection;

    private basicSetup = false;

    /**
     * l10n Service for internationalization.
     */
    private readonly l10nService: L10nService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'virtualservice',
            windowElement: 'lazy-load',
            objectType: VirtualService,
            permissionName: AviPermissionResource.PERMISSION_VIRTUALSERVICE,
            whitelistedFields: [
                'content_rewrite',
                'analytics_policy',
                'vh_matches',
                'markers',
                'http_policies',
            ],
            restrictEditOnEssentialLicense: false,
            ...args,
        };

        super(extendedArgs);

        const ApplicationProfileCollection =
            this.getNgDependency(APPLICATION_PROFILE_COLLECTION_TOKEN);

        this.l10nService = this.getNgDependency('l10nService');
        this.httpPolicySets = [this.getNgDependency('httpPolicySets')];
        this.networkSecurityPolicy = this.getNgDependency('NetworkSecurityPolicy');

        this.ApplicationProfileCollection = new ApplicationProfileCollection();

        this.onApplicationTypeChange$ = new BehaviorSubject<boolean>(false);

        this.isApplicationProfileLoaded$ = new BehaviorSubject<boolean>(false);

        this.onVsTypeHostingChange$ =
            new BehaviorSubject<VirtualServiceType | undefined>(undefined);

        this.onVsHostingTypeChange$ = new BehaviorSubject<VHType | undefined>(undefined);
        this.onVsNameChange$ = new BehaviorSubject<string | undefined>(undefined);
        this.onVrfContextAndCloudSet$ = new BehaviorSubject<boolean>(false);
        this.onApplicationProfileTypeChange$ =
            new BehaviorSubject<ApplicationProfileType | undefined>(undefined);

        this.onWafPolicyItemChange$ = new BehaviorSubject<WafPolicyItem | undefined>(undefined);
        this.devLoggerService = this.getNgDependency('devLoggerService');

        this.applicationProfileType$ = combineLatest([
            this.isApplicationProfileLoaded$,
            this.onApplicationProfileTypeChange$,
        ]).pipe(
            map(([isAppProfileLoaded, selectedAppProfile]: [boolean, ApplicationProfileType]) => {
                return isAppProfileLoaded ? this.appType() : selectedAppProfile;
            }),
        );
    }

    /**
     * @override
     * VS config modified before saving.
     */
    public dataToSave(): IVsMacroData {
        const config = super.dataToSave();

        if (!this.isHTTP()) {
            delete config.saml_sp_config;
            delete config.jwt_config;
            delete config.ldap_vs_config;
            delete config.oauth_vs_config;
            delete config.sso_policy_ref;
            delete config.waf_policy_ref;
            delete config.csrf_policy_ref;
            delete config.bot_policy_ref;
            delete config.icap_request_profile_refs;
            delete config.http_policies;
        }

        const vsUriPath = `${VS_ENDPOINT}/${this.id ?? ''}`;

        return {
            data: config,
            uri_path: vsUriPath,
        };
    }

    /**
     * @override
     * URL to which api calls are fired, on save.
     * config of obj_ref_data is created as new object, avoding multiple api calls.
     */
    public urlToSave(): string {
        return '/api/macro';
    }

    public static getDefaultServiceConfig(config: any = null): any {
        if (config) {
            // set SSL on for 443
            if (config.port === 443 && !('enable_ssl' in config)) {
                config.enable_ssl = true;
            }

            // set port_range_end to port by default
            if (!('port_range_end' in config) && 'port' in config) {
                config.port_range_end = config.port;
            }
        }

        return extend({
            port: 80,
            port_range_end: 80,
            enable_ssl: false,
            nwOverride: false,
        }, config);
    }

    /** @override */
    public destroy(): boolean {
        this.isApplicationProfileLoaded$.complete();
        this.onApplicationTypeChange$.complete();
        this.onVsTypeHostingChange$.complete();
        this.onVsHostingTypeChange$.complete();
        this.onVsNameChange$.complete();
        this.onVrfContextAndCloudSet$.complete();
        this.onApplicationProfileTypeChange$.complete();
        this.onWafPolicyItemChange$.complete();

        return super.destroy();
    }

    public get applicationProfileItem(): ApplicationProfile | undefined {
        return this.ApplicationProfileCollection?.items[0];
    }

    public appType(): ApplicationProfileType | undefined {
        return this.vsAppProfileRef && this.applicationProfileItem?.getType();
    }

    public get vsAppProfileRef(): string {
        return this.config.application_profile_ref;
    }

    public getAppProfileConfig(): IApplicationProfile {
        return this.applicationProfileItem?.config;
    }

    /**
     * Informs if icap logs are present.
     * @returns {boolean}
     */
    public hasIcapProfile(): boolean {
        return Boolean(this.getConfig().icap_request_profile_refs?.length);
    }

    /**
     * Informs if bot policy logs are present.
     * @returns {boolean}
     */
    public hasBotPolicy(): boolean {
        return Boolean(this.getConfig().bot_policy_ref);
    }

    /**
     * Returns true if dataScripts can be attached to a virtual service.
     * @return {boolean}
     */
    public hasDataScriptSupport(): boolean {
        const appProfileType = this.appType();

        return appProfileType !== ApplicationProfileType.APPLICATION_PROFILE_TYPE_SIP;
    }

    /**
     * Returns default service port for this VS. It depends on application profile type.
     * @returns {number}
     */
    public getDefaultServicePort(): number {
        const appProfileType = this.appType();

        // HTTPS and HTTP application profiles have the same type of HTTP
        const port = this.isHTTPS() ? 443 : ApplicationProfile.getDefaultServicePort(appProfileType);

        return port;
    }

    /**
     * Returns true for VSes with SSL app profile type or HTTP VS with at least one service
     * using SSL.
     * @returns {boolean}
     * @public
     */
    // FIXME doesn't work for VS_TYPE_VH_CHILD VSes since they don't have services list
    public sslEnabled(): boolean {
        const config = this.getConfig();

        if (config) {
            if (this.appType() === ApplicationProfileType.APPLICATION_PROFILE_TYPE_SSL) {
                return true;
            } else if (this.isHTTP()) {
                return some(config.services, service => service.enable_ssl);
            }
        }

        return false;
    }

    public updateIsSslEnabledInServicePorts(): void {
        this.isSslEnabledInServicePorts = this.config.services?.some(
            (service: IService) => service.enable_ssl,
        );
    }

    public policyEnabled(): any {
        return some(this.httpPolicySets, policySet => policySet.hasRules());
    }

    /**
     * Returns true for HTTP(S) VSes only.
     * @returns {boolean}
     * @public
     */
    public isHTTP(): boolean {
        return this.appType() === ApplicationProfileType.APPLICATION_PROFILE_TYPE_HTTP;
    }

    /**
     * Returns true for HTTPS VSes. Requires appProfile data loaded.
     * @returns {boolean}
     */
    public isHTTPS(): boolean {
        if (this.isHTTP()) {
            const { http_profile: httpProfile } = this.getAppProfileConfig();

            return ApplicationProfile
                .getHttpProfileSslEverywhereEnabledValue(httpProfile);
        }

        return false;
    }

    /**
     * Add a new http policy set.
     */
    public addHttpPolicySet(): void {
        this.config.http_policies.add();
    }

    /**
     * Delete http policy set.
     */
    public deleteHttpPolicySet(row: MessageItem<IHTTPPolicies>): void {
        this.config.http_policies.removeByMessageItem(row);
    }

    /**
     * Returns true for VS having DNS application profile.
     * @returns {boolean}
     * @public
     */
    public isDNS(): boolean {
        return this.appType() === ApplicationProfileType.APPLICATION_PROFILE_TYPE_DNS;
    }

    public getCandidateServiceEnginesAndHosts(vipId: string): any {
        const api =
            // eslint-disable-next-line max-len
            `/api/${this.objectName}/${this.id}/candidatesehostlist?include_name&vip_id=${vipId}`;

        return this.request('GET', api)
            .then(({ data }) => {
                if (Array.isArray(data) && data.length) {
                    return data[0];
                }

                return {};
            });
    }

    /**
     * Import lazy-loaded modal component.
     */
    // eslint-disable-next-line class-methods-use-this
    public async getModalComponent(windowElement: TWindowElement): Promise<Type<Component>> {
        const { VirtualServiceModalComponent, VirtualServiceBasicModalComponent } = await import(
            /* webpackChunkName: "virtual-service-modal" */
            // eslint-disable-next-line max-len
            'ng/lazy-loaded-components/modals/virtual-service-modal/virtual-service-modal.component'
        );

        return (
            this.basicSetup ? VirtualServiceBasicModalComponent : VirtualServiceModalComponent
        ) as Type<Component>;
    }

    /**
     * Load application profile that is attached to VS.
     */
    public readonly loadApplicationProfileData = debounce((): void => {
        const uuid = this.stringService.slug(this.config.application_profile_ref);

        this.ApplicationProfileCollection.setParams({ uuid });

        this.isApplicationProfileLoaded$.next(false);
        this.ApplicationProfileCollection.load().then(() => {
            this.isApplicationProfileLoaded$.next(true);
        });
        this.ApplicationProfileCollection.on('collectionLoadFail', () => {
            const customErrorMessage = 'Application Profile failed to load. Please check access permissions.';

            this.errors = Array.isArray(this.errors) ? this.errors.concat(customErrorMessage) : customErrorMessage;
            console.error(customErrorMessage);
        });
    }, APPLICATION_PROFILE_LOADING_DEBOUNCE_VALUE);

    public isVsBeingEdited(): boolean {
        return Boolean(this.id);
    }

    public isVrfAndCloudSet(): boolean {
        return Boolean(this.config.vrf_context_ref && this.config.cloud_ref);
    }

    public notifySubscribersOnWafPolicyItemChange(itemsMap: Map<string, WafPolicyItem>): void {
        const wafPolicyItem = itemsMap.get(this.config.waf_policy_ref);

        this.onWafPolicyItemChange$.next(wafPolicyItem);
    }

    /**
     * Return boolean if type if HTTP post change of ApplicationType change in VS.
     */
    public notifySubscribersOnVsApplicationTypeChange(applicationType: ApplicationProfileType): void {
        const isHttpType = applicationType === ApplicationProfileType.APPLICATION_PROFILE_TYPE_HTTP;

        this.onApplicationTypeChange$.next(isHttpType);
    }

    public notifySubscribersOnVsTypeHostingChange(): void {
        this.onVsTypeHostingChange$.next(this.config.type);
    }

    public notifySubscribersOnVsHostingTypeChange(): void {
        this.onVsHostingTypeChange$.next(this.config.vh_type);
    }

    public notifySubscribersOnVsNameChange(): void {
        this.onVsNameChange$.next(this.config.name);
    }

    public notifySubscribersOnVrfContextAndCloudSet(): void {
        this.onVrfContextAndCloudSet$.next(true);
    }

    public notifySubscribersOnApplicationProfileTypeChange(
        appProfileType: ApplicationProfileType,
    ): void {
        this.onApplicationProfileTypeChange$.next(appProfileType);
    }

    public addVHMatch(): void {
        this.addChildMessageItem({
            field: 'vh_matches',
            modalBindings: {
                isEditing: false,
                hostNames: this.getVHMatchesHostNames(),
                hostIndex: this.config.vh_matches.count,
            },
        });
    }

    public editVHMatch(vhMatch: VHMatchConfigItem): void {
        this.editChildMessageItem({
            field: 'vh_matches',
            messageItem: vhMatch,
            modalBindings: {
                isEditing: true,
                hostNames: this.getVHMatchesHostNames(),
                hostIndex: this.config.vh_matches.getArrayIndexWithMessageItem(vhMatch),
            },
        });
    }

    public deleteVHMatch(vhMatch: VHMatchConfigItem): void {
        this.config.vh_matches.removeByMessageItem(vhMatch);
    }

    /** @override */
    public drop(force: boolean, params: IVSDropParams): IPromise<IHttpResponse<IVirtualService>> {
        let promise = super.drop(force, params);

        if (params?.withChildren) {
            const deletionGroups = [];

            const { poolgroups, pools } = this.data;
            const { vsvip_ref: vsVipRef } = this.getConfig();

            if (vsVipRef) {
                deletionGroups.push([vsVipRef]);
            }

            if (Array.isArray(poolgroups) && poolgroups.length) {
                deletionGroups.push(poolgroups);
            }

            if (Array.isArray(pools) && pools.length) {
                deletionGroups.push(pools);
            }

            deletionGroups.forEach(childRefs => {
                promise = promise.then(
                    () => this.deleteChildren(childRefs),
                ) as IPromise<IHttpResponse<any>>;
            });
        }

        return promise;
    }

    /**
     * Set and save an enabled state of VS.
     */
    public setEnabledState(enabled: boolean): IPromise<IHttpResponse<IVirtualService>> {
        return this.patch({ replace: { enabled } });
    }

    /**
     * VS uses macro API to save, let's use direct VS api for patch requests.
     * @override
     */
    // eslint-disable-next-line no-underscore-dangle
    public patchRequest_(payload: unknown): IBaseRequestPromise<unknown> {
        return this.request(
            'patch',
            `${VS_ENDPOINT}/${this.id}?include_name`,
            payload,
            null,
            'patch',
        );
    }

    /**
     * Return the cloud reference configured for the VS.
     */
    public getCloudRef(): string {
        const { cloud_ref: cloudRef } = this.getConfig();

        return cloudRef ?? '';
    }

    /**
     * Return the VRF context ref configured for the VS.
     */
    public getVRFContextRef(): string {
        const { vrf_context_ref: vrfContextRef } = this.getConfig();

        return vrfContextRef ?? '';
    }

    /**
     * Return type of VS, either with virtual hosting or normal, assuming type was returned by
     * BE API.
     */
    public getType(): VirtualServiceType {
        return this.getConfig().type;
    }

    /**
     * Check if VS is a virtual hosting child.
     */
    public isVHChild(): boolean {
        return this.getType() === VirtualServiceType.VS_TYPE_VH_CHILD;
    }

    /**
     * Add headers for GSLB VS.
     */
    public addGslbHeaderParams(clusterUuid: string): void {
        const headersParams = {
            headers_: { 'X-Avi-Internal-GSLB': clusterUuid },
        };

        this.addParams(headersParams);
    }

    /**
     * Return flat list of addresses based on VS VIP ref data.
     */
    public getAllVsVipAddresses(): string[] {
        const { vsvip_ref_data: vsVip } = this.getConfig();
        const addresses = vsVip?.vip.reduce((acc, vip) => {
            const {
                floating_ip: floatingIp,
                ip_address: ip,
                ip6_address: ip6,
            } = vip;

            if (floatingIp?.addr) {
                acc.push(floatingIp.addr);
            }

            if (ip?.addr) {
                acc.push(ip.addr);
            }

            if (ip6?.addr) {
                acc.push(ip6.addr);
            }

            return acc;
        }, []) || [];

        return addresses.sort((ipaddressA, ipaddressB) => naturalSort(ipaddressA, ipaddressB));
    }

    /**
     * Return IP addresses of parent VS for VH child.
     */
    public getAllVHParentVsVipAddresses(): Promise<string[]> {
        this.busy = true;

        return new Promise(resolve => {
            this.busy = true;

            this.loadVhParent()
                .then(parentVS => {
                    const parentIPs = parentVS.getAllVsVipAddresses();

                    resolve(parentIPs);
                    parentVS.destroy();
                })
                .finally(() => this.busy = false);
        });
    }

    /**
     * Reset Virtual Hosting Parent Vs Ref.
     */
    public resetVhParentVsRef(): void {
        delete this.config.vh_parent_vs_ref;
    }

    /**
     * Remove all the Vh matches.
     */
    public removeAllVhMatches(): void {
        this.config.vh_matches.removeAll();
    }

    /**
     * Remove all the Vh Domain Names.
     */
    public removeAllVhDomainNames(): void {
        this.config.vh_domain_name.length = 0;
    }

    /**
     * @override
     */
    public edit(
        windowElement?: string | Type<Component>,
        editParams: IItemEditParams & { basicSetup?: boolean } = {},
    ): IPromise<void> {
        this.basicSetup = Boolean(editParams?.basicSetup);

        return super.edit(windowElement, editParams);
    }

    /**
     * Method to load side preview.
     * @override
     */
    protected async getFullModalProps(
        params: IItemParams,
        modalComponent?: Type<Component>,
    ): Promise<IFullModalLayout> {
        const props = await super.getFullModalProps(params, modalComponent);
        const { previewComponent: defaultPreviewComponent, componentProps: { basicSetup } } = props;
        const previewComponent =
            basicSetup ? defaultPreviewComponent : VirtualServicePreviewComponent as Type<Component>;

        return {
            ...props,
            previewComponent,
            previewComponentProps: {
                config: params.editable.getConfig(),
                isItem: params.editable instanceof VirtualServiceItem,
                item: params.editable as VirtualServiceItem,
            },
        };
    }

    /** @override */
    protected requiredFields(): string[] {
        return [
            'analytics_policy',
            'markers',
            'content_rewrite',
            'http_policies',
        ];
    }

    /** @override */
    protected getModalBreadcrumbTitle(): string {
        return this.l10nService.getMessage(globalL10nKeys.virtualServiceLabel);
    }

    private getVHMatchesHostNames(): string[] {
        return this.config.vh_matches.config.map((match: VHMatchConfigItem) => match.config.host);
    }

    /**
     * Delete the children objects associated with the VS being deleted.
     */
    private deleteChildren(refs: string[]): Promise<void | Array<IHttpResponse<any>>> {
        const { devLoggerService } = this;
        const deleteRequests = refs.map(ref => this.request('delete', ref));

        return Promise.all(deleteRequests).catch(
            ({ data: errors }) => devLoggerService.error(errors),
        );
    }

    /**
     * Return parent VS ref for virtual hosting child.
     */
    private getVHParentRef(): string {
        return this.getConfig().vh_parent_vs_ref;
    }

    /**
     * Load the virtual host parent VS and return it. To be called only when this is a child VS.
     */
    private loadVhParent(): Promise<VirtualServiceItem> {
        return new Promise((resolve, reject) => {
            if (!this.isVHChild()) {
                reject(new Error('Not a VH child VS.'));
            }

            const parentRef = this.getVHParentRef();

            // API virtualservice-inventory does not provide vh_parent_vs_ref field.
            // In case of VS collection grid we do not need parent reference
            // as we are not showing parent Service Port or IPs.
            if (!parentRef) {
                reject(new Error('VH parent reference not available.'));
            }

            const stringService = this.getNgDependency(StringService);

            // eslint-disable-next-line no-extra-parens
            const parentVS = new (this.constructor as typeof VirtualServiceItem)({
                id: stringService.slug(parentRef),
                params: {
                    join: 'vsvip_ref',
                },
            });

            const headerParams = this.getLoadHeaders_();

            parentVS.addParams({ headers_: headerParams });
            parentVS.load().then(() => resolve(parentVS));
        });
    }
}
